import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios'
import { useAuthStore } from '@/stores/auth-store'
import type { ApiResponse, ApiError, PaginatedResponse } from '@/types/api'
import type { AuthTokens } from '@/types/auth'

/**
 * API client configuration
 */
interface ApiClientConfig {
  baseURL: string
  timeout: number
}

/**
 * Default configuration
 */
const defaultConfig: ApiClientConfig = {
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '/api',
  timeout: 30000, // 30 seconds
}

/**
 * Flag to prevent multiple token refresh attempts
 */
let isRefreshing = false
let refreshSubscribers: Array<(token: string) => void> = []

/**
 * Subscribe to token refresh
 */
function subscribeToTokenRefresh(callback: (token: string) => void) {
  refreshSubscribers.push(callback)
}

/**
 * Notify all subscribers with new token
 */
function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach((callback) => callback(token))
  refreshSubscribers = []
}

/**
 * Refresh the access token using the refresh token
 */
async function refreshAccessToken(
  client: AxiosInstance
): Promise<AuthTokens | null> {
  const { tokens } = useAuthStore.getState()

  if (!tokens?.refreshToken) {
    return null
  }

  try {
    const response = await client.post<ApiResponse<AuthTokens>>(
      '/auth/refresh',
      { refreshToken: tokens.refreshToken },
      {
        // Skip the auth interceptor for this request
        headers: { 'X-Skip-Auth': 'true' },
      }
    )

    if (response.data.success && response.data.data) {
      const newTokens = response.data.data
      useAuthStore.getState().setTokens(newTokens)
      return newTokens
    }

    return null
  } catch {
    // Refresh failed, logout the user
    useAuthStore.getState().logout()
    return null
  }
}

/**
 * Create the API client instance
 */
function createApiClient(config: ApiClientConfig = defaultConfig): AxiosInstance {
  const client = axios.create({
    baseURL: config.baseURL,
    timeout: config.timeout,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // Request interceptor - inject JWT token and tenant ID
  client.interceptors.request.use(
    (requestConfig: InternalAxiosRequestConfig) => {
      // Skip auth for specific requests
      if (requestConfig.headers?.['X-Skip-Auth']) {
        delete requestConfig.headers['X-Skip-Auth']
        return requestConfig
      }

      const { tokens } = useAuthStore.getState()

      if (tokens?.accessToken) {
        requestConfig.headers.Authorization = `Bearer ${tokens.accessToken}`
      }

      // Add tenant ID header (default tenant UUID from seed)
      // TODO: Make this configurable or store in auth state
      requestConfig.headers['X-Tenant-Id'] = 'default-tenant'

      return requestConfig
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  // Response interceptor - handle 401 and token refresh
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiResponse<unknown>>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean
      }

      // Handle 401 Unauthorized
      if (error.response?.status === 401 && !originalRequest._retry) {
        // Skip refresh for auth endpoints
        if (
          originalRequest.url?.includes('/auth/login') ||
          originalRequest.url?.includes('/auth/refresh')
        ) {
          return Promise.reject(error)
        }

        originalRequest._retry = true

        if (!isRefreshing) {
          isRefreshing = true

          try {
            const newTokens = await refreshAccessToken(client)

            if (newTokens) {
              isRefreshing = false
              onTokenRefreshed(newTokens.accessToken)

              // Retry the original request with new token
              originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`
              return client(originalRequest)
            }
          } catch {
            isRefreshing = false
            refreshSubscribers = []
            useAuthStore.getState().logout()
          }
        }

        // Wait for token refresh to complete
        return new Promise((resolve, reject) => {
          subscribeToTokenRefresh((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            resolve(client(originalRequest))
          })

          // Timeout after 10 seconds
          setTimeout(() => {
            reject(new Error('Token refresh timeout'))
          }, 10000)
        })
      }

      // Transform error to standard format
      const errorDetails = error.response?.data?.error?.details
      const apiError: ApiError = {
        code: error.response?.data?.error?.code ?? 'UNKNOWN_ERROR',
        message:
          error.response?.data?.error?.message ??
          error.message ??
          'An unexpected error occurred',
        statusCode: error.response?.status ?? 500,
        ...(errorDetails != null ? { details: errorDetails } : {}),
      }

      return Promise.reject(apiError)
    }
  )

  return client
}

/**
 * Singleton API client instance
 */
export const apiClient = createApiClient()

/**
 * Typed request helpers
 */
export async function get<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.get<ApiResponse<T>>(url, config)
  return response.data.data
}

export async function post<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.post<ApiResponse<T>>(url, data, config)
  return response.data.data
}

export async function put<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.put<ApiResponse<T>>(url, data, config)
  return response.data.data
}

export async function patch<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.patch<ApiResponse<T>>(url, data, config)
  return response.data.data
}

export async function del<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.delete<ApiResponse<T>>(url, config)
  return response.data.data
}

/**
 * GET helper for paginated endpoints.
 * Backend paginated responses have shape { success, data, pagination }
 * which differs from the standard { success, data } wrapper.
 * This returns { data, pagination } matching PaginatedResponse<T>.
 */
export async function getPaginated<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<PaginatedResponse<T>> {
  const response = await apiClient.get(url, config)
  const body = response.data as { success: boolean } & PaginatedResponse<T>
  return { data: body.data, pagination: body.pagination }
}

// Re-export isApiError from types for convenience
export { isApiError } from '@/types/api'
